import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import store from './store';
import { assessmentRoutes } from '@/products/pha/modules/assessment/router';
import { assessmentFormRoutes } from '@/products/pha/modules/assessment-form/router';
import { answerRoutes, userAssessmentRoutes } from '@/products/pha/modules/answers/router';
import { UserPermissions } from './enums';
import { checkPermission } from './helpers/permission-checker';

const routes = [
    {
        path: '/',
        name: 'app',
        component: App,
        children: [
            {
                path: '/',
                name: 'Home',
                exact: true,
                component: () => import('./products/pha/modules/dashboard/pages/StaticDashboard.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Favourites', label: 'Dashboard' }],
                    requiresAuth: true,
                },
            },
            // {
            //     path: '/dashboard',
            //     name: 'ActiveDashboard',
            //     component: () => import('./products/pha/modules/dashboard/pages/Dashboard.vue'),
            //     meta: {
            //         breadcrumb: [{ parent: 'Favourites', label: 'Active Dashboard' }],
            //         requiresAuth: true,
            //     },
            // },

            {
                path: '/company',
                name: 'CompanyList',
                props: true,
                component: () => import('./products/pha/modules/company/views/CompanyList.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Company', label: 'All Companies' }],
                    requiresAuth: true,
                    permission: [UserPermissions.READ_COMPANY],
                },
            },
            {
                path: '/company/edit/:id',
                name: 'CompanyUpdate',
                props: true,
                component: () => import('./products/pha/modules/company/views/CompanyCreateUpdate.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Company', label: 'Edit Company' }],
                    permission: [UserPermissions.WRITE_COMPANY],
                },
            },
            {
                path: '/company/:id/logo',
                name: 'company-logo',
                component: () => import('./products/pha/modules/company/views/CompanyLogo.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Company', label: 'Upload Company Logo' }],
                    permission: [UserPermissions.WRITE_COMPANY],
                },
            },
            {
                path: '/company/create',
                name: 'CompanyCreate',
                props: true,
                component: () => import('./products/pha/modules/company/views/CompanyCreateUpdate.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Company', label: 'Create Company' }],
                    permission: [UserPermissions.WRITE_COMPANY],
                },
            },

            {
                path: '/license',
                name: 'LicenseList',
                props: true,
                component: () => import('./products/pha/modules/license/view/LicenseList.vue'),
                meta: {
                    breadcrumb: [{ parent: 'License', label: 'All Licenses' }],
                },
            },
            {
                path: '/license/edit/:id',
                name: 'LicenseUpdate',
                props: true,
                component: () => import('./products/pha/modules/license/view/LicenseCreateUpdate.vue'),
                meta: {
                    breadcrumb: [{ parent: 'License', label: 'Edit License' }],
                },
            },

            {
                path: '/form/cst_v1/:id',
                name: 'cst_v1_assessment',
                props: true,
                component: () => import('./products/pha/modules/cstAssessment/view/cst_v1_assessment.vue'),
                meta: {
                    breadcrumb: [{ parent: 'ASSESSMENTS FORM', label: 'ANSWER ASSESSMENT' }],
                },
            },

            {
                path: '/form/hmi/insight_portfolio',
                name: 'InsightPortfolio',
                props: true,
                component: () => import('./products/pha/modules/hmi/views/HmiReportInsightPortfolio.vue'),
                meta: {
                    breadcrumb: [{ parent: 'ASSESSMENTS', label: 'HMI Report for Insight Portfolio' }],
                },
            },

            {
                path: '/manageDashboards',
                children: [
                    {
                        path: '',
                        name: 'ManageDashboard',
                        exact: true,
                        component: () => import('./products/pha/modules/dashboard/pages/ManageDashboard.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Dashboard', label: 'All Dashboards' }],
                            permission: [UserPermissions.READ_METRIC],
                        },
                    },
                    {
                        path: 'edit/:id',
                        name: 'EditDashboard',
                        exact: true,
                        props: true,
                        component: () => import('./products/pha/modules/dashboard/components/Edit.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Dashboard', label: 'Edit Dashboard' }],
                            permission: [UserPermissions.WRITE_METRIC],
                        },
                    },
                    {
                        path: 'create',
                        name: 'CreateDashboard',
                        exact: true,
                        props: true,
                        component: () => import('./products/pha/modules/dashboard/components/CreateUpdateDashboard.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Dashboard', label: 'Edit Dashboard' }],
                            permission: [UserPermissions.WRITE_METRIC],
                        },
                    },
                    {
                        path: 'update/:id',
                        name: 'UpdateDashboard',
                        exact: true,
                        props: true,
                        component: () => import('./products/pha/modules/dashboard/components/CreateUpdateDashboard.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Dashboard', label: 'Edit Dashboard' }],
                            permission: [UserPermissions.WRITE_METRIC],
                        },
                    },
                ],
            },
            {
                path: '/companySelect',
                name: 'CompanySelect',
                exact: true,
                component: () => import('./components/CompanySelect.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Company', label: 'Company Selection' }],
                    requiresAuth: true,
                },
            },
            {
                path: '/pool',
                name: 'ManagePool',
                exact: true,
                component: () => import('./products/pha/modules/pool/views/ManagePool'),
                meta: {
                    breadcrumb: [{ parent: 'Company', label: 'Company Pools' }],
                    requiresAuth: true,
                },
            },
            {
                path: `/attributes`,
                name: 'attributes',
                component: () => import('./products/pha/modules/attribute/views/AttributeIndex.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Attribute', label: 'All Attributes' }],
                    requiresAuth: true,
                    permission: [UserPermissions.READ_ASSESSMENT_ATTRIBUTES],
                },
            },
            {
                path: '/blocks',
                children: [
                    {
                        path: '',
                        name: 'Blocks',
                        component: () => import('./components/block/Index.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Block', label: 'All Blocks' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_ASSESSMENT_BLOCKS],
                        },
                    },
                    {
                        path: 'addBlock',
                        component: () => import('./components/block/AddBlock.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Block', label: 'Add Block' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_ASSESSMENT_BLOCKS],
                        },
                    },
                    {
                        path: 'editBlock/:shared/:id',
                        component: () => import('./components/block/AddBlock.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Block', label: 'Edit Block' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_ASSESSMENT_BLOCKS],
                        },
                    },
                    {
                        path: 'boxBuilder',
                        children: [
                            {
                                path: ':blockId',
                                name: 'BoxBuilder',
                                component: () => import('./pages/box-builder/Index.vue'),
                                meta: {
                                    breadcrumb: [{ parent: 'Block', label: 'Block Containers and Elements' }],
                                    requiresAuth: true,
                                    permission: [UserPermissions.WRITE_ASSESSMENT_BLOCKS],
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: '/formulas',
                children: [
                    {
                        path: '',
                        name: 'Formula',
                        component: () => import('./pages/formula/Index.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Formula', label: 'All Formulas' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_FORMULAS],
                        },
                    },
                    {
                        path: 'add',
                        name: 'AddFormula',
                        component: () => import('./pages/formula/AddFormula.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Formula', label: 'Add Formula' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_FORMULAS],
                        },
                    },
                    {
                        path: ':id/edit/:shared',
                        props: true,
                        name: 'EditFormula',
                        component: () => import('./pages/formula/EditFormula.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Formula', label: 'Edit Formula' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_FORMULAS],
                        },
                    },
                ],
            },
            {
                path: '/assessments',
                children: assessmentRoutes,
            },
            {
                path: '/form',
                children: assessmentFormRoutes,
            },
            {
                path: '/answers',
                children: answerRoutes,
            },
            {
                path: '/users',
                children: [
                    {
                        path: '',
                        name: 'ManageUsers',
                        component: () => import('./pages/user/ManageUser.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User', label: 'All Users' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USERS],
                        },
                    },
                    {
                        path: ':userId',
                        children: userAssessmentRoutes,
                    },
                ],
            },
            {
                path: '/datasource',
                children: [
                    {
                        path: '',
                        name: 'ManageDataSource',
                        component: () => import('./products/pha/modules/datasource/views/DataSourceIndex.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Data Sources', label: 'All Data Sources' }],
                            requiresAuth: true,
                            // permission: [UserPermissions.READ_],
                        },
                    },
                    {
                        path: 'configure/:id',
                        props: true,
                        name: 'DataSourceLinks',
                        component: () => import('./products/pha/modules/datasource/views/DataSourceConfig.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Data Sources', label: 'Configure Data Source Links' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_USER_GROUP],
                        },
                    },
                    {
                        path: 'edit/:id',
                        props: true,
                        name: 'DataSourceUpdate',
                        component: () => import('./products/pha/modules/datasource/views/DataSourceUpdate.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Data Sources', label: 'Configure Data Source Links' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_USER_GROUP],
                        },
                    },
                ],
            },
            {
                path: '/metricSpecialGroups',
                children: [
                    {
                        path: '',
                        name: 'SpecialUserGroups',
                        component: () => import('./pages/user-groups/SpecialUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'All User Groups' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                        },
                    },
                    {
                        path: 'view/:id',
                        name: 'ViewSpecialUserGroups',
                        props: true,
                        component: () => import('./pages/user-groups/CreateUpdateSpecialUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'Update User Group' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                            readonly: true,
                        },
                    },
                    {
                        path: 'update/:id',
                        name: 'UpdateSpecialUserGroups',
                        props: true,
                        component: () => import('./pages/user-groups/CreateUpdateSpecialUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'Update User Group' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                        },
                    },
                    {
                        path: 'create',
                        name: 'CreateSpecialUserGroups',
                        props: true,
                        component: () => import('./pages/user-groups/CreateUpdateSpecialUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'Create User Group' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                        },
                    },
                ]
            },
            {
                path: '/metricGroups',
                children: [
                    {
                        path: '',
                        name: 'DatasourceUserGroups',
                        component: () => import('./pages/user-groups/ManageUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'All User Groups' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                        },
                    },
                    {
                        path: 'view/:id',
                        name: 'ViewDatasourceUserGroups',
                        props: true,
                        component: () => import('./pages/user-groups/ViewDatasourceUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'Update User Group' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                        },
                    },
                    {
                        path: 'update/:id',
                        name: 'UpdateDatasourceUserGroups',
                        props: true,
                        component: () => import('./pages/user-groups/CreateUpdateUserGroups.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'User Groups', label: 'Update User Group' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_USER_GROUP],
                        },
                    },
                ],
            },
            {
                path: '/metrics',
                children: [
                    {
                        path: '',
                        name: 'ManageMetrics',
                        component: () => import('./pages/metrics/ManageMetrics.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Metrics', label: 'All Metrics' }],
                            requiresAuth: true,
                            permission: [UserPermissions.READ_METRIC],
                        },
                    },
                    {
                        path: 'update/:id',
                        name: 'UpdateMetric',
                        props: true,
                        component: () => import('./components/metrics/CreateUpdateMetric.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Metrics', label: 'Update Metric' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_METRIC],
                        },
                    },
                    {
                        path: 'create',
                        name: 'CreateMetric',
                        props: true,
                        component: () => import('./components/metrics/CreateUpdateMetric.vue'),
                        meta: {
                            breadcrumb: [{ parent: 'Metrics', label: 'Create Metric' }],
                            requiresAuth: true,
                            permission: [UserPermissions.WRITE_METRIC],
                        },
                    },
                ],
            },
            {
                path: '/permissionGroups',
                name: 'ManagePermissionGroups',
                component: () => import('./products/modules/groups/ManageGroup.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Settings', label: 'Manage Groups' }],
                    requiresAuth: true,
                    permission: [UserPermissions.READ_GROUPS],
                },
            },
            {
                path: '/userPermissionGroups',
                name: 'ManageUserPermissionGroups',
                component: () => import('./products/modules/groups/ManageUsersGroup.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Settings', label: 'Manage User Groups' }],
                    requiresAuth: true,
                    permission: [UserPermissions.READ_USER_GROUP],
                },
            },
            {
                path: '/permissions',
                name: 'Permissions',
                component: () => import('./products/modules/permissions/Permission.vue'),
                meta: {
                    breadcrumb: [{ parent: 'Settings', label: 'Permissions List' }],
                    requiresAuth: true,
                    permission: [UserPermissions.READ_PERMISSIONS],
                },
            },
        ],
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('./pages/Login.vue'),
    },
    {
        path: '/auth/callback',
        name: 'AuthCallback',
        component: () => import('./pages/AuthCallback.vue'),
    },
    {
        path: '/setPassword',
        name: 'SetPassword',
        component: () => import('./pages/SetPassword.vue'),
    },
    {
        path: '/forgotPassword',
        name: 'ForgotPassword',
        component: () => import('./pages/ForgotPassword.vue'),
    },
    {
        path: '/404/unauthorized',
        name: 'Unauthorized',
        component: () => import('./pages/NoAccess.vue'),
    },
    {
        path: '/error',
        name: 'error',
        component: () => import('./pages/Error.vue'),
    },
    {
        path: '/:catchAll(.*)',
        name: 'notfound',
        component: () => import('./pages/NotFound.vue'),
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { left: 0, top: 0 };
    },
});

router.beforeEach((to, from, next) => {
    const checkToken = store.getters['auth/isLoggedIn'];
    const company = store.getters['auth/userCompanyId'];
    const isGodUser = store.getters['auth/isGodUser'];
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const userPermissions = store.getters['auth/getUserPermissions']; // list of permissions of the user
    // lists out the permissions required to access that router
    const permissionsRequired = to?.meta?.permission;
    // if (!requiresAuth && checkToken && to.name !== 'Home') {
    //     // Check if the user is authenticated and has a valid token
    //     // If not, redirect to login page
    //     router.push({ name: 'Home' });
    // } else
    if (requiresAuth && !checkToken) {
        // Check if the user is authenticated and has a valid token
        // If not, redirect to login page
        router.push({ name: 'login' });
    } else if (checkToken && isGodUser && company == null && to.name !== 'CompanySelect') {
        // Additional checks for special cases, like selecting a company
        router.push({ name: 'CompanySelect' });
    } else if (permissionsRequired && permissionsRequired?.length > 0) {
        // checks if user have the permission to access the route
        // loop through each permissionsRequired and check if user have those permissions
        // if (permissionsRequired?.length > 0) {
        let authorized = true;
        permissionsRequired.forEach((permissionRequired) => {
            // set authorized to false if any of the permission is missing
            if (!checkPermission(userPermissions, permissionRequired)) {
                authorized = false;
            }
        });
        if (!authorized) {
            router.push({ name: 'Unauthorized' });
        }
        next();
    } else {
        // User is authenticated and authorized, continue to the requested page
        next();
    }
});
export default router;
